import React, { useEffect, useState } from 'react';
import AddCompetitionForm from './AddCompetition';
import EditCompetitionForm from './EditCompetitionForm';
import '../css/ParticipantList.css';
import '../css/CompetitionList.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrashAlt, faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';

const CompetitionList = () => {
  const [competitions, setCompetitions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editModal, setEditModal] = useState({ show: false, id: null });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    fetchCompetitions();
  }, []);

  const fetchCompetitions = async () => {
    try {
      const res = await axios.get(`${API_URL}/ems/competitions`);
      if (Array.isArray(res.data)) setCompetitions(res.data);
    } catch (err) {
      console.error('Failed to fetch competitions', err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date
      .toLocaleDateString('en-GB', { month: 'short', day: '2-digit', year: 'numeric' })
      .replace(',', '');
  };

  const handleEdit = (id) => {
    setEditModal({ show: true, id });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this competition?')) {
      try {
        await axios.delete(`${API_URL}/ems/competitions/${id}`);
        fetchCompetitions();
      } catch {
        alert('Delete failed');
      }
    }
  };

  const toggleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key ? (prev.direction === 'asc' ? 'desc' : 'asc') : 'asc',
    }));
  };

  const sortIcon = (key) => {
    if (sortConfig.key !== key) return faSort;
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  };

  const sorted = sortConfig.key
    ? [...competitions].sort((a, b) => {
        let aVal, bVal;
        if (sortConfig.key === 'start_date') {
          aVal = new Date(a.start_date || 0).getTime();
          bVal = new Date(b.start_date || 0).getTime();
          return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
        } else if (sortConfig.key === 'status') {
          aVal = a.status ? 1 : 0;
          bVal = b.status ? 1 : 0;
          return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
        } else {
          aVal = (a[sortConfig.key] || '').toLowerCase();
          bVal = (b[sortConfig.key] || '').toLowerCase();
          const result = aVal.localeCompare(bVal);
          return sortConfig.direction === 'asc' ? result : -result;
        }
      })
    : competitions;

  // Pagination calculations
  const effectivePerPage = itemsPerPage >= sorted.length ? Math.max(sorted.length, 1) : itemsPerPage;
  const totalPages = Math.max(Math.ceil(sorted.length / effectivePerPage), 1);
  const indexOfLast = currentPage * effectivePerPage;
  const indexOfFirst = indexOfLast - effectivePerPage;
  const currentItems = sorted.slice(indexOfFirst, indexOfLast);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const getVisiblePages = () => {
    if (totalPages <= 5) return pageNumbers;
    if (currentPage <= 3) return pageNumbers.slice(0, 5);
    if (currentPage >= totalPages - 2) return pageNumbers.slice(-5);
    return pageNumbers.slice(currentPage - 3, currentPage + 2);
  };

  return (
    <div className="p-3 ems-competitions-content" style={{ minHeight: '100vh' }}>
      {/* Table */}
        <div className="competition-table-header">
          <div
            className={`sort-header ${sortConfig.key === 'name' ? 'sorted' : ''}`}
            onClick={() => toggleSort('name')}
          >
            Name <FontAwesomeIcon icon={sortIcon('name')} className="sort-icon" />
          </div>
          <div
            className={`sort-header ${sortConfig.key === 'start_date' ? 'sorted' : ''}`}
            onClick={() => toggleSort('start_date')}
          >
            Date <FontAwesomeIcon icon={sortIcon('start_date')} className="sort-icon" />
          </div>
          <div
            className={`sort-header ${sortConfig.key === 'location' ? 'sorted' : ''}`}
            onClick={() => toggleSort('location')}
          >
            Location <FontAwesomeIcon icon={sortIcon('location')} className="sort-icon" />
          </div>
          <div
            className={`sort-header ${sortConfig.key === 'status' ? 'sorted' : ''}`}
            onClick={() => toggleSort('status')}
          >
            Status <FontAwesomeIcon icon={sortIcon('status')} className="sort-icon" />
          </div>
          <div>Actions</div>
        </div>

        {sorted.length === 0 ? (
          <div className="p-4 text-center text-muted">
            No competitions available
          </div>
        ) : (
          currentItems.map((competition) => (
            <div className="competition-table-row" key={competition._id}>
              <div className="competition-cell-name">
                {competition.logo && (
                  <img
                    src={`${API_URL}/ems/${competition.logo.replace(/\\/g, '/')}`}
                    alt=""
                    className="competition-logo-inline"
                  />
                )}
                <span>{competition.name || '-'}</span>
              </div>
              <div>
                {formatDate(competition.start_date)} to {formatDate(competition.end_date)}
              </div>
              <div>{competition.location || '-'}</div>
              <div>
                <span className={`competition-status-badge ${competition.status ? 'competition-status-active' : 'competition-status-inactive'}`}>
                  {competition.status ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="competition-actions">
                <button
                  className="btn btn-sm action-btn"
                  onClick={() => handleEdit(competition._id)}
                  title="Edit Competition"
                >
                  <FontAwesomeIcon icon={faPen} />
                </button>
                <button
                  className="btn btn-sm action-btn"
                  onClick={() => handleDelete(competition._id)}
                  title="Delete"
                >
                  <FontAwesomeIcon icon={faTrashAlt} />
                </button>
              </div>
            </div>
          ))
        )}

        {/* Pagination inside table */}
        {sorted.length > 0 && (
          <div className="participant-pagination">
            <div className="d-flex align-items-center gap-2">
              <span style={{ fontSize: '13px', color: '#666' }}>Show</span>
              <select
                className="form-select form-select-sm"
                style={{ width: 'auto', fontSize: '13px' }}
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            <div className="pagination-box">
              <button
                className="pagination-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </button>

              {getVisiblePages().map((num) => (
                <button
                  key={num}
                  className={`pagination-btn ${num === currentPage ? 'active' : ''}`}
                  onClick={() => setCurrentPage(num)}
                >
                  {num}
                </button>
              ))}

              <button
                className="pagination-btn"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}

      {/* Add Competition Button */}
      <div className="d-flex justify-content-end" style={{ marginTop: '20px' }}>
        <button
          onClick={() => setShowModal(true)}
          style={{
            background: '#000',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '5px',
            border: 'none',
            marginTop: '35px',
            fontWeight: 600,
          }}
        >
          + Add Competition
        </button>
      </div>

      {/* ADD competition MODAL */}
      {showModal && (
        <AddCompetitionForm
          onClose={() => setShowModal(false)}
          oncompetitionCreated={fetchCompetitions}
        />
      )}

      {/* EDIT competition MODAL */}
      {editModal.show && (
        <EditCompetitionForm
          id={editModal.id}
          onClose={() => setEditModal({ show: false, id: null })}
          onUpdated={fetchCompetitions}
        />
      )}
    </div>
  );
};

export default CompetitionList;
